import React, { useState } from "react";
import styles from "./ChangePassword.module.scss";
import { Button, CircularProgress, TextField } from "@mui/material";
import {
  CurrentPasswordIcon,
  NewPasswordIcon,
  PasswordHide,
  PasswordView,
} from "@features/common/SvgIcons";
import { useAppDispatch } from "@redux/hooks";
import { notifyUser } from "@redux/slices/CommonSlice";
import { useChangePasswordMutation } from "../settingsAPI";

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [isCurrentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const [changePassword, { isLoading: isChangePasswordLoading }] =
    useChangePasswordMutation();
  const dispatch = useAppDispatch();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    delete error[name];
  };

  const handleValidate = () => {
    const errorData: { [key: string]: string } = {};
    const { confirmPassword, currentPassword, newPassword } = formData;

    if (!confirmPassword) {
      errorData["confirmPassword"] = "Confirm Password is required";
    }

    if (!currentPassword) {
      errorData["currentPassword"] = "Current Password is required";
    }
    if (!newPassword) {
      errorData["newPassword"] = "New Password is required";
    }

    if (newPassword !== confirmPassword) {
      errorData["confirmPassword"] = "Passwords do not match";
    }

    setError(errorData);
    return Object.keys(errorData).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!handleValidate() || isButtonDisabled || isChangePasswordLoading)
      return;

    try {
      const body = {
        old_password: formData.currentPassword,
        new_password1: formData.newPassword,
        new_password2: formData.confirmPassword,
      };

      const response: any = await changePassword(body).unwrap();

      dispatch(
        notifyUser({
          title: "Password Updated",
          message: response?.message,
          severity: "success",
        })
      );

      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setError({});
    } catch (error: any) {
      const errors = error?.data?.errors;

      if (errors) {
        // 1. Initialize an empty object to collect errors
        const newErrorState: { [key: string]: string } = {};

        // 2. Conditionally append values if they exist in the backend response
        if (errors.old_password) {
          newErrorState.currentPassword = errors.old_password[0];
        }

        if (errors.password) {
          newErrorState.newPassword = errors.password[0];
        }

        // 3. Update the state once with the collected values
        setError(newErrorState);
        return;
      }

      dispatch(
        notifyUser({
          title: "Error",
          message: error?.data?.message || "Something went wrong",
          severity: "error",
        })
      );
    }
  };

  const isButtonDisabled =
    !formData.currentPassword ||
    !formData.newPassword ||
    !formData.confirmPassword ||
    !!error?.currentPassword ||
    !!error?.newPassword ||
    !!error?.confirmPassword;

  return (
    <div className={styles["change-password"]}>
      <form onSubmit={handleSubmit}>
        <div className={styles["input-wrapper"]}>
          <div className={styles["input-wrapper__body"]}>
            <div
              className={`${styles["password"]} ${
                error?.currentPassword && styles["password--error"]
              }`}
            >
              <div
                className={`settings-password-input ${
                  isCurrentPasswordVisible && "settings-password-input--text"
                }  ${styles["password__input"]}`}
              >
                <CurrentPasswordIcon />
                <TextField
                  variant="standard"
                  name="currentPassword"
                  type={isCurrentPasswordVisible ? "text" : "password"}
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  placeholder="Current Password"
                />
                {formData?.currentPassword?.length > 0 && (
                  <div
                    onClick={() => setCurrentPasswordVisible((prev) => !prev)}
                    className={styles["password__show-button"]}
                  >
                    {!isCurrentPasswordVisible ? (
                      <PasswordHide />
                    ) : (
                      <PasswordView />
                    )}
                  </div>
                )}
              </div>
            </div>
            {error?.currentPassword && (
              <div className={styles["error-message"]}>
                {error.currentPassword}
              </div>
            )}
          </div>
          <div className={styles["input-wrapper__body"]}>
            <div
              className={`${styles["password"]} ${
                error?.newPassword && styles["password--error"]
              }`}
            >
              <div
                className={`settings-password-input ${
                  isNewPasswordVisible && "settings-password-input--text"
                }  ${styles["password__input"]}`}
              >
                <NewPasswordIcon />
                <TextField
                  variant="standard"
                  name="newPassword"
                  type={isNewPasswordVisible ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="New Password"
                />
                {formData?.newPassword?.length > 0 && (
                  <div
                    onClick={() => setNewPasswordVisible((prev) => !prev)}
                    className={styles["password__show-button"]}
                  >
                    {!isNewPasswordVisible ? (
                      <PasswordHide />
                    ) : (
                      <PasswordView />
                    )}
                  </div>
                )}
              </div>
            </div>
            {error?.newPassword && (
              <div className={styles["error-message"]}>{error.newPassword}</div>
            )}
          </div>
          <div className={styles["input-wrapper__body"]}>
            <div
              className={`${styles["password"]} ${
                error?.confirmPassword && styles["password--error"]
              }`}
            >
              <div
                className={`settings-password-input ${
                  isConfirmPasswordVisible && "settings-password-input--text"
                }  ${styles["password__input"]}`}
              >
                <NewPasswordIcon />
                <TextField
                  variant="standard"
                  name="confirmPassword"
                  type={isConfirmPasswordVisible ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm Password"
                />
                {formData?.confirmPassword?.length > 0 && (
                  <div
                    onClick={() => setConfirmPasswordVisible((prev) => !prev)}
                    className={styles["password__show-button"]}
                  >
                    {!isConfirmPasswordVisible ? (
                      <PasswordHide />
                    ) : (
                      <PasswordView />
                    )}
                  </div>
                )}
              </div>
            </div>
            {error?.confirmPassword && (
              <div className={styles["error-message"]}>
                {error.confirmPassword}
              </div>
            )}
          </div>
        </div>

        <div
          className={`settings-submit-button ${
            isChangePasswordLoading
              ? styles["change-password__button-loader"]
              : styles["change-password__save-button"]
          }  ${
            isButtonDisabled && styles["change-password__save-button--disabled"]
          } `}
        >
          {isChangePasswordLoading ? (
            <CircularProgress
              sx={{
                color: "#C028B9",
              }}
            />
          ) : (
            <Button type="submit" disabled={isButtonDisabled}>
              Save
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
